// Utility API per la gestione del Bazar
// Tutte le chiamate vengono eseguite tramite API routes lato server

/**
 * Recupera tutti gli oggetti bazar con filtri e paginazione
 * @param {Object} filters - Oggetto con filtri: { categoria, condizione, stato, prezzoMin, prezzoMax, negoziabile, search }
 * @param {number} page - Numero pagina (default: 1)
 * @param {number} pageSize - Dimensione pagina (default: 50)
 */
export const fetchBazars = async (filters = {}, page = 1, pageSize = 50) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    // Aggiungi i filtri se presenti
    if (filters.categoria) {
      params.append('categoria', filters.categoria);
    }

    if (filters.condizione) {
      params.append('condizione', filters.condizione);
    }

    if (filters.stato) {
      if (Array.isArray(filters.stato)) {
        filters.stato.forEach((stato) => params.append('stato', stato));
      } else {
        params.append('stato', filters.stato);
      }
    }

    if (filters.prezzoMin) {
      params.append('prezzoMin', filters.prezzoMin.toString());
    }

    if (filters.prezzoMax) {
      params.append('prezzoMax', filters.prezzoMax.toString());
    }

    if (filters.negoziabile === true) {
      params.append('negoziabile', 'true');
    }

    if (filters.search) {
      params.append('search', filters.search);
    }

    if (filters.mine === true || filters.mine === 'true') {
      params.append('mine', '1');
    }

    const response = await fetch(`/api/bazar?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Errore nel recupero degli oggetti bazar');
    }

    const data = await response.json();
    return {
      bazars: data.bazars || [],
      pagination: data.pagination || {},
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nel fetch degli oggetti bazar:', error);
    throw error;
  }
};

/**
 * Recupera un singolo oggetto bazar per ID
 * @param {string} id - Document ID dell'oggetto bazar
 */
export const fetchBazarById = async (id) => {
  try {
    const response = await fetch(`/api/bazar/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Oggetto non trovato');
      }
      throw new Error("Errore nel recupero dell'oggetto bazar");
    }

    const data = await response.json();
    return data.bazar;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Errore nel fetch dell'oggetto bazar:", error);
    throw error;
  }
};

/**
 * Crea un nuovo oggetto bazar
 * @param {Object} bazarData - Dati dell'oggetto bazar
 */
export const createBazar = async (bazarData) => {
  try {
    const response = await fetch('/api/bazar', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(bazarData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || "Errore nella creazione dell'oggetto bazar"
      );
    }

    const data = await response.json();
    return data.bazar;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Errore nella creazione dell'oggetto bazar:", error);
    throw error;
  }
};

/**
 * Aggiorna un oggetto bazar esistente
 * @param {string} id - Document ID dell'oggetto bazar
 * @param {Object} bazarData - Dati aggiornati dell'oggetto bazar
 */
export const updateBazar = async (id, bazarData) => {
  try {
    const response = await fetch(`/api/bazar/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(bazarData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || "Errore nell'aggiornamento dell'oggetto bazar"
      );
    }

    const data = await response.json();
    return data.bazar;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Errore nell'aggiornamento dell'oggetto bazar:", error);
    throw error;
  }
};

/**
 * Elimina un oggetto bazar
 * @param {string} id - Document ID dell'oggetto bazar
 */
export const deleteBazar = async (id) => {
  try {
    const response = await fetch(`/api/bazar/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || "Errore nell'eliminazione dell'oggetto bazar"
      );
    }

    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Errore nell'eliminazione dell'oggetto bazar:", error);
    throw error;
  }
};
